// One demo's input + run state. Sample = cached GET; own file = Turnstile ->
// upload -> Turnstile -> run -> poll (spec §3.3). Results are kept per demo so
// moving between stops 1 and 2 (same run) never re-fetches.
import { useCallback, useEffect, useRef, useState } from 'react'

import * as api from '@/lib/api'
import { type Stop } from '@/lib/demos'
import { getTurnstileToken } from '@/lib/turnstile'

import type { DemoId, DemoParams, RunResponse, UploadResult } from '@/shared/contract'

export type Input =
  | { source: 'sample'; file: string }
  | { source: 'own'; file: File; upload?: UploadResult }

export interface RunState {
  status: 'idle' | 'uploading' | 'running' | 'done' | 'error'
  step: number
  startedAt?: number
  result?: RunResponse
  /** Compose returns bytes, not a run. */
  bytes?: ArrayBuffer
  error?: string
  /** Params the visible result was produced with (stale check for re-runs). */
  params?: DemoParams[DemoId]
  /** Input the result was produced from; restored with the result on a stop switch. */
  input?: Input
}

const sampleInput = (stop: Stop): Input =>
  stop.sample
    ? { source: 'sample', file: stop.sample.file }
    : { source: 'own', file: new File([], '') }

const results = new Map<string, RunState>() // ponytail: module cache, survives stop switches

export function useDemoRun(stop: Stop) {
  const [input, setInput] = useState<Input>(
    () => results.get(stop.demo)?.input ?? sampleInput(stop),
  )
  const [params, setParams] = useState<DemoParams[DemoId]>(stop.defaultParams)
  const [state, setState] = useState<RunState>(
    () => results.get(stop.demo) ?? { status: 'idle', step: 0 },
  )
  const abort = useRef(0)
  const prevId = useRef<string | undefined>(undefined)
  const isSwitching = prevId.current !== stop.id

  // During the first render after a stop switch, `params`/`input`/`state`
  // still hold the previous stop's values until the effect below runs.
  // Derive the correct values for this render so CodePanel never sees stale
  // params (stale `{}` for extract caused `sq(j(undefined))` crash).
  const effectiveParams = isSwitching ? stop.defaultParams : params
  const effectiveInput = isSwitching ? (results.get(stop.demo)?.input ?? sampleInput(stop)) : input
  const effectiveState = isSwitching
    ? (results.get(stop.demo) ?? { status: 'idle', step: 0 })
    : state

  // Switching stop: reset params, restore any cached result (with its input) for that demo.
  useEffect(() => {
    if (prevId.current === stop.id) return
    prevId.current = stop.id
    abort.current++
    const cached = results.get(stop.demo)
    setInput(cached?.input ?? sampleInput(stop))
    setParams(stop.defaultParams)
    setState(cached ?? { status: 'idle', step: 0 })
    // Compose has no sample file but the proxy still serves a cached sample run.
    if (!cached && (stop.sample || stop.kind === 'none')) void loadSample(stop)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stop.id])

  const commit = (demo: DemoId, next: RunState, from: Input) => {
    if (next.status === 'done') results.set(demo, { ...next, input: from })
    setState(next)
  }

  async function loadSample(s: Stop) {
    const token = ++abort.current
    setState({ status: 'running', step: 0, startedAt: Date.now() })
    try {
      const result = await api.getSample(s.demo)
      if (token !== abort.current) return
      if (result.run.status !== 'processed') {
        const err = result.run.error as { message?: string } | undefined
        setState({
          status: 'error',
          step: 0,
          error: err?.message ?? 'Sample run did not finish. Try again later.',
        })
        return
      }
      commit(
        s.demo,
        { status: 'done', step: s.steps.length, result, params: s.defaultParams },
        sampleInput(s),
      )
    } catch (e) {
      if (token === abort.current) setState({ status: 'error', step: 0, error: message(e) })
    }
  }

  /** Run live: sample id with edited params, or the own file. */
  const runLive = useCallback(
    async (opts: { sitekey: string; params?: DemoParams[DemoId] }) => {
      const token = ++abort.current
      const p = opts.params ?? effectiveParams
      const startedAt = Date.now()
      try {
        let fileId: string | undefined
        let uploaded: UploadResult | undefined
        let from = effectiveInput
        if (effectiveInput.source === 'own' && stop.kind !== 'none') {
          setState({ status: 'uploading', step: 0, startedAt })
          const ts = await getTurnstileToken(opts.sitekey)
          uploaded =
            (effectiveInput as Extract<Input, { source: 'own' }>).upload ??
            (await api.upload(effectiveInput.file, stop.demo, ts))
          if (token !== abort.current) return
          from = { ...effectiveInput, upload: uploaded }
          setInput(from)
          fileId = uploaded.fileId
        } else if (stop.kind !== 'none') {
          fileId = `sample:${stop.demo}`
        }
        setState({ status: 'running', step: 1, startedAt })
        const ts2 = await getTurnstileToken(opts.sitekey)
        if (stop.demo === 'compose-invoice') {
          const { bytes, cost } = await api.compose(
            (p as DemoParams['compose-invoice']).data ?? {},
            ts2,
          )
          if (token !== abort.current) return
          commit(
            stop.demo,
            {
              status: 'done',
              step: stop.steps.length,
              bytes,
              result: { run: { id: 'compose', status: 'processed' }, cost },
              params: p,
            },
            from,
          )
          return
        }
        let result = await api.run({ demo: stop.demo, fileId, params: p, turnstile: ts2 })
        // Step ticker advances on each poll; the run body has no step field.
        let step = 2
        // ponytail: ~5 min ceiling (15 x wait=20); cold Modal boots take ~3 min.
        for (let polls = 0; !api.isTerminal(result.run.status); polls++) {
          if (token !== abort.current) return
          if (polls >= 15) {
            setState({
              status: 'error',
              step,
              error: 'Still processing after 5 minutes. Try again later.',
            })
            return
          }
          setState({ status: 'running', step: Math.min(step++, stop.steps.length - 1), startedAt })
          // oxlint-disable-next-line no-await-in-loop -- long-poll until terminal
          result = await api.getRun(result.run.id)
        }
        if (token !== abort.current) return
        if (result.run.status !== 'processed') {
          const err = result.run.error as { message?: string } | undefined
          setState({ status: 'error', step, error: err?.message ?? `Run ${result.run.status}` })
          return
        }
        commit(stop.demo, { status: 'done', step: stop.steps.length, result, params: p }, from)
      } catch (e) {
        if (token === abort.current) setState({ status: 'error', step: 0, error: message(e) })
      }
    },
    [effectiveInput, effectiveParams, stop],
  )

  const chooseOwn = (file: File) => {
    abort.current++
    setInput({ source: 'own', file })
    setState({ status: 'idle', step: 0 })
  }
  const chooseSample = () => {
    if (!stop.sample) return
    setInput({ source: 'sample', file: stop.sample.file })
    const cached = results.get(stop.demo)
    const hit = cached?.input?.source === 'sample' ? cached : undefined
    setState(hit ?? { status: 'idle', step: 0 })
    if (!hit) void loadSample(stop)
  }

  return {
    input: effectiveInput,
    params: effectiveParams,
    setParams,
    state: effectiveState,
    runLive,
    chooseOwn,
    chooseSample,
  }
}

function message(e: unknown): string {
  if (e instanceof api.ApiError) return e.error.message
  if (e instanceof Error)
    return e.message === 'turnstile_failed' ? 'Verification failed. Try again.' : e.message
  return 'Something went wrong'
}
